import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Box } from 'src/components/Box';
import { SelectField } from 'src/components/SelectNew';
import { Text } from 'src/components/Text';
import { messages } from 'src/messages';
import { ResourceName, Subscription, WebsiteKind } from 'src/orchd-client';
import { useGetBrandingQuery } from 'src/store/branding/query';
import { sessionSelectors } from 'src/store/session/selectors';
import { getSubscriptionResource, isUnlimitedResourceTotal } from 'src/utils/packages/utils';
import sortBy from 'lodash/sortBy';
import { Radio } from 'src/components/Radio';
import { FormType } from 'src/containers/Form';
import { GithubWebsiteDeployFormShape } from './DeployToWebsite';
import { TextInputField } from 'src/components/TextInput';

interface Props {
  form: FormType<GithubWebsiteDeployFormShape>;
  subscriptions: Subscription[];
}

export const NewWebsiteFields = ({ form, subscriptions }: Props) => {
  const { isMasterOrg } = useSelector(sessionSelectors.getMember);

  const { data: { stagingDomain } = {} } = useGetBrandingQuery();

  const { formatMessage } = useIntl();

  const currentSubscriptionId = form.values?.newWebsite?.subscriptionId;

  const stagingResource =
    subscriptions &&
    subscriptions
      .find((sub) => sub.id === currentSubscriptionId)
      ?.resources.find((resource) => resource.name === ResourceName.stagingWebsites);

  const productionResource =
    subscriptions &&
    subscriptions
      .find((sub) => sub.id === currentSubscriptionId)
      ?.resources.find((resource) => resource.name === ResourceName.websites);

  const stagingTotal = stagingResource ? stagingResource.total || -1 : 0;
  const stagingUsage = stagingResource ? stagingResource.usage : 0;

  const productionTotal = productionResource ? productionResource.total || -1 : 0;
  const productionUsage = productionResource ? productionResource.usage : 0;

  const showStaging = stagingDomain && (isMasterOrg || stagingTotal === -1 || stagingTotal > 0);

  const submitDisabledStaging =
    form.values?.newWebsite?.kind === WebsiteKind.staging &&
    stagingUsage >= stagingTotal &&
    stagingTotal !== -1 &&
    !isMasterOrg;
  const submitDisabledProduction =
    !(form.values?.newWebsite?.kind === WebsiteKind.staging) &&
    productionUsage >= productionTotal &&
    productionTotal !== -1 &&
    !isMasterOrg;

  if (stagingResource && submitDisabledProduction && !submitDisabledStaging) {
    form.setFieldValue('isStaging', true);
  }

  const getRemainingWebsites = (subscription: Subscription) => {
    const resource = getSubscriptionResource(subscription, ResourceName.websites);

    if (!resource) {
      return 0;
    }

    if (isUnlimitedResourceTotal(resource.total)) {
      return -1;
    }

    const remaining = (resource.total || 0) - resource.usage;

    return remaining || 0;
  };

  const subscriptionsOptions = useMemo(
    () =>
      sortBy(subscriptions, (subscription) => -getRemainingWebsites(subscription)).map((subscription) => {
        const disabled = !!subscription.suspendedBy;

        return {
          value: subscription.id,
          label: subscription.friendlyName || subscription.planName,
          isDisabled: disabled,
          data: subscription,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptions]
  );

  const getSubscriptionUsage = (subscription: Subscription) => {
    const resource = getSubscriptionResource(subscription, ResourceName.websites);

    if (!resource) {
      return { usage: 0, total: 0 };
    }

    if (isUnlimitedResourceTotal(resource.total)) {
      return { usage: resource.usage, total: -1 };
    }

    return { usage: resource.usage, total: resource.total || 0 };
  };

  const subscriptionFieldValue = useMemo(
    () =>
      subscriptionsOptions.find((option) => option.value === currentSubscriptionId) ?? {
        label: undefined,
        value: undefined,
      },
    [subscriptionsOptions, currentSubscriptionId]
  );

  // Hide subscription selector if only one subscription
  const showSubscriptionSelector = !isMasterOrg && subscriptions && subscriptions.length > 1;

  return (
    <Box>
      {showSubscriptionSelector && (
        <Box mb="xl">
          <SelectField
            name="newWebsite.subscriptionId"
            placeholder={formatMessage(messages.select_a_package)}
            label={formatMessage(messages.package)}
            value={[subscriptionFieldValue]}
            err={form.errors?.newWebsite?.subscriptionId}
            touched={form.touched?.newWebsite?.subscriptionId}
            onChange={({ name, value }) => {
              //form.setFieldValue('isStaging', false);
              form.setFieldValue(name, value[0].value);
              form.setFieldValue('newWebsite.serverGroupId', undefined);
            }}
            options={subscriptionsOptions}
            requiredIndicator
            searchable
            estimatedOptionHeight={56}
            renderOption={(option) => {
              const subscription = option.option.data as Subscription;
              const isDisabled = option.option.isDisabled;
              const search = option.search;

              return (
                <Box>
                  <Box wrap="nowrap" j="space-between">
                    <Text ellipsis fullWidth color={isDisabled ? 'disabled' : 'dark'} highlightText={search}>
                      {subscription.friendlyName || subscription.planName}
                    </Text>
                  </Box>

                  <Text size="sm" color="grey">
                    <FormattedMessage
                      id="websites.push_live_modal.website_usage"
                      defaultMessage="{usage} of {total, plural, =-1 {∞} other {#}} websites used"
                      values={{ ...getSubscriptionUsage(subscription) }}
                    />
                  </Text>
                </Box>
              );
            }}
          />
        </Box>
      )}
      {showStaging && (
        <Box mb="xl">
          <Box d="column">
            <Radio
              name="isStaging"
              onChange={() => {
                form.setFieldValue('newWebsite.kind', WebsiteKind.normal);
              }}
              checked={!(form.values.newWebsite?.kind === WebsiteKind.staging)}
              label={
                <Box el="span" gap="xs" a="baseline">
                  <Text tag="span">
                    <FormattedMessage
                      id="websites.add_website.kind_radios.production"
                      defaultMessage="Production website"
                    />
                  </Text>
                  {showSubscriptionSelector && (
                    <Text
                      size="sm"
                      color={productionTotal === -1 || productionUsage < productionTotal ? 'grey' : 'error'}
                    >
                      (
                      <FormattedMessage
                        {...messages.usage_of_total_infinite}
                        values={{
                          usage: productionResource?.usage ?? 0,
                          total: productionTotal,
                        }}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.used} />)
                    </Text>
                  )}
                </Box>
              }
              help={formatMessage({
                id: 'websites.add_website.kind_radios.production.help',
                defaultMessage: 'A live website on its own domain name',
              })}
            />
            <Radio
              name="isStaging"
              marginBottom="0"
              onChange={() => {
                form.setFieldValue('newWebsite.kind', WebsiteKind.staging);
              }}
              checked={form.values.newWebsite?.kind === WebsiteKind.staging}
              label={
                <Box el="span" gap="xs" a="baseline">
                  <Text tag="span">
                    <FormattedMessage id="websites.add_website.kind_radios.staging" defaultMessage="Staging website" />
                  </Text>

                  {showSubscriptionSelector && (
                    <Text size="sm" color={stagingTotal === -1 || stagingUsage < stagingTotal ? 'grey' : 'error'}>
                      (
                      <FormattedMessage
                        {...messages.usage_of_total_infinite}
                        values={{
                          total: stagingTotal,
                          usage: stagingResource?.usage ?? 0,
                        }}
                      />
                      &nbsp;
                      <FormattedMessage {...messages.used} />)
                    </Text>
                  )}
                </Box>
              }
              help={formatMessage(
                {
                  id: 'websites.add_website.kind_radios.staging.help',
                  defaultMessage: 'A test website, suffixed with our staging domain {stagingDomain}',
                },
                { stagingDomain }
              )}
            />
          </Box>
        </Box>
      )}
      <Box mb="xl">
        <TextInputField
          name="newWebsite.domain"
          prefix={form.values.newWebsite?.kind === WebsiteKind.staging ? '' : 'www.'}
          suffix={form.values.newWebsite?.kind === WebsiteKind.staging ? `.${stagingDomain}` : ''}
          value={form.values.newWebsite?.domain}
          onChange={(e) => {
            form.handleChange(e);
            form.setFieldValue('newWebsite.domain', (e.target.value as string).toLowerCase());
          }}
          onBlur={form.handleBlur}
          label={<FormattedMessage {...messages.domain} />}
          err={form.errors?.newWebsite?.domain}
          touched={form.touched?.newWebsite?.domain}
          requiredIndicator
          suffixMaxWidth="50%"
        />
      </Box>
    </Box>
  );
};
